// Bridge that lets the React runner talk to its native host.
//
// Hosts we support:
//   * iOS WKWebView   — channels live under `window.webkit.messageHandlers.<channel>`.
//   * Android WebView — channels live under `window.AletheiaBridge.<channel>` (added in Phase 2).
//   * Browser (dev)    — falls back to `window.parent.postMessage`, useful when running
//                        the standalone Vite app inside another iframe (e.g. Storybook).
//
// The schema for each channel is in src/types.ts (AttemptEnvelope, HapticEnvelope).

type Channel = "attempt" | "haptic" | "ready" | "log";

interface IOSWebKit {
  messageHandlers?: Record<string, { postMessage(payload: unknown): void } | undefined>;
}

interface AndroidBridge {
  [channel: string]: ((json: string) => void) | undefined;
}

interface BridgeWindow extends Window {
  webkit?: IOSWebKit;
  AletheiaBridge?: AndroidBridge;
}

export function postToHost(channel: Channel, message: unknown): void {
  const w = window as BridgeWindow;

  // iOS WKWebView
  const iosHandler = w.webkit?.messageHandlers?.[channel];
  if (iosHandler && typeof iosHandler.postMessage === "function") {
    try {
      iosHandler.postMessage(message);
    } catch (err) {
      console.warn(`[aletheia-runner] iOS postMessage failed on '${channel}':`, err);
    }
    return;
  }

  // Android WebView (stub for Phase 2)
  const androidHandler = w.AletheiaBridge?.[channel];
  if (typeof androidHandler === "function") {
    try {
      androidHandler(JSON.stringify(message));
    } catch (err) {
      console.warn(`[aletheia-runner] Android bridge failed on '${channel}':`, err);
    }
    return;
  }

  // Browser fallback (dev / standalone)
  try {
    window.parent.postMessage({ channel, message }, "*");
  } catch {
    // Last-resort: just log.
    // eslint-disable-next-line no-console
    console.log(`[aletheia-runner] (no host) channel='${channel}' payload=`, message);
  }
}

/**
 * Lets the host know the bundle is ready to receive interactions.
 * Hosts can use this to hide a "Loading…" splash they showed while WKWebView
 * was warming up.
 */
export function postReady(): void {
  postToHost("ready", { timestamp: Date.now() });
}
